import { useEffect, useState } from "react";
import { ArrowUpRight, Clock, MapPin, Briefcase } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { ShareJob } from "@/components/ShareJob";
import { SUPPORT_EMAIL, fetchJobs } from "@/lib/api";

const MISSION = "Join CreJik's mission";
const DESCRIPTION = "Join CreJik's mission of bringing Citizens together. Remote internships at TruCitizen — apply in two minutes.";

const Opening = ({ o, i, origin }) => {
  const url = `${origin}/careers#${o.slug}`;
  return (
    <Reveal delay={0.1 + i * 0.08}>
      <article id={o.slug} className="glass rounded-3xl p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10 scroll-mt-32 transition-transform duration-300 hover:-translate-y-1" data-testid={`career-card-${o.slug}`}>
        <div className="flex-1 min-w-0">
          <p className="eyebrow mb-2">{o.team}</p>
          <h2 className="font-display font-bold text-xl sm:text-2xl text-[var(--ink)]" data-testid={`career-title-${o.slug}`}>{o.title}</h2>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold text-[var(--ink-soft)]">
            <span className="inline-flex items-center gap-1.5" data-testid={`career-duration-${o.slug}`}><Clock size={15} className="text-[var(--saffron)]" /> Duration: {o.duration}</span>
            <span className="inline-flex items-center gap-1.5" data-testid={`career-location-${o.slug}`}><MapPin size={15} className="text-[var(--green)]" /> Location: {o.location}</span>
            <span className="inline-flex items-center gap-1.5"><Briefcase size={15} className="text-[var(--muted)]" /> Internship</span>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 self-start lg:self-center">
          <a href={o.form_url} target="_blank" rel="noopener noreferrer" className="btn-saffron" data-testid={`career-apply-${o.slug}`}>Apply <ArrowUpRight size={18} /></a>
          <ShareJob id={o.slug} title={o.title} url={url} text={`${MISSION} — ${o.title} (${o.duration}, ${o.location}). Apply here:`} />
        </div>
      </article>
    </Reveal>
  );
};

export default function Careers() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://trucitizen.in";
  const [jobs, setJobs] = useState(null);

  useEffect(() => { fetchJobs().then(setJobs).catch(() => setJobs([])); }, []);
  useEffect(() => {
    if (!jobs?.length || !window.location.hash) return;
    document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [jobs]);

  return (
    <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-32 sm:pt-36 pb-16 sm:pb-28" data-testid="careers-page">
      <SEO title="Careers | TruCitizen" description={DESCRIPTION} path="/careers" image={`${origin}/og-careers.jpg`}
        jsonLd={(jobs || []).map((o) => ({
          "@context": "https://schema.org", "@type": "JobPosting", title: o.title, datePosted: o.created_at.slice(0, 10),
          description: `${o.title} internship at CreJik (TruCitizen) — ${o.duration}, ${o.location}. ${MISSION} of bringing Citizens together.`, employmentType: "INTERN",
          hiringOrganization: { "@type": "Organization", name: "CreJik Technologies Private Limited", sameAs: "https://trucitizen.in" },
          jobLocationType: "TELECOMMUTE", applicantLocationRequirements: { "@type": "Country", name: "India" }, directApply: true, url: o.form_url,
        }))} />
      <Reveal>
        <p className="eyebrow mb-4">Careers</p>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[var(--ink)]" data-testid="careers-title">Join CreJik's mission of bringing Citizens together</h1>
      </Reveal>
      <div className="mt-12 space-y-6" data-testid="careers-list">
        {jobs === null && [0, 1].map((i) => <div key={i} className="h-40 rounded-3xl bg-white/50 animate-pulse" />)}
        {jobs?.length === 0 && (
          <div className="glass rounded-3xl p-8 text-[var(--ink-soft)]" data-testid="careers-empty">No open roles right now — check back soon or write to us below.</div>
        )}
        {jobs?.map((o, i) => <Opening key={o.id} o={o} i={i} origin={origin} />)}
      </div>
      <Reveal delay={0.3}>
        <p className="mt-12 text-sm text-[var(--muted)]" data-testid="careers-footer-note">
          Don't see your role? Write to <a href={`mailto:${SUPPORT_EMAIL}?subject=%5BTruCitizen%5D%20Careers`} className="text-[var(--saffron-deep)] underline underline-offset-2" data-testid="careers-email-link">{SUPPORT_EMAIL}</a> with the subject <span className="font-mono text-[var(--ink)]">[TruCitizen] Careers</span>.
        </p>
      </Reveal>
    </section>
  );
}
